import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useBrand, BrandLogo } from "@/lib/brand";
import { Loader2, Lock, ArrowRight, Calendar, Send, MessageSquare, Sparkles, Clock, CircleDot, TrendingUp } from "lucide-react";

const SYMS = { USD:"$", EUR:"€", GBP:"£", NGN:"₦", ZAR:"R", KES:"KSh", GHS:"₵", EGP:"£", INR:"₹", BRL:"R$", MXN:"$", AED:"AED ", SAR:"SAR ", JPY:"¥", CNY:"¥", AUD:"A$", CAD:"C$", SGD:"S$" };

const fmtCurrency = (n, sym = "$", decimals = 1) => {
  if (typeof n !== "number" || isNaN(n)) return "—";
  if (n >= 1e9) return `${sym}${(n / 1e9).toFixed(decimals)}B`;
  if (n >= 1e6) return `${sym}${(n / 1e6).toFixed(decimals)}M`;
  return `${sym}${Math.round(n).toLocaleString()}`;
};

const STATUS_COLORS = {
  open: "text-zinc-300 border-zinc-700",
  qualified: "text-[#FFB000] border-[#FFB000]/40",
  negotiating: "text-[#00BFFF] border-[#00BFFF]/40",
  won: "text-[#00E676] border-[#00E676]/40",
  lost: "text-[#FF3B30] border-[#FF3B30]/40",
};

export default function DealRoomPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const { resolveBy } = useBrand();
  const k = sp.get("k") || "";
  const [room, setRoom] = useState(null);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [posting, setPosting] = useState(false);
  const threadRef = useRef(null);

  const isOwner = !!localStorage.getItem("dara_token");

  const fetchRoom = async () => {
    try {
      const headers = isOwner ? { Authorization: `Bearer ${localStorage.getItem("dara_token")}` } : {};
      const r = await axios.get(`${API}/deal-rooms/${slug}`, { params: { k }, headers });
      setRoom(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load deal room.");
    }
  };

  const fetchMessages = async () => {
    try {
      const headers = isOwner ? { Authorization: `Bearer ${localStorage.getItem("dara_token")}` } : {};
      const r = await axios.get(`${API}/deal-rooms/${slug}/messages`, { params: { k }, headers });
      setMessages(r.data.items);
    } catch { /* swallow */ }
  };

  useEffect(() => { resolveBy("deal-room", slug); fetchRoom(); fetchMessages(); /* eslint-disable-next-line */ }, [slug]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const post = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const headers = isOwner
        ? { Authorization: `Bearer ${localStorage.getItem("dara_token")}` }
        : {};
      await axios.post(`${API}/deal-rooms/${slug}/messages`, {
        body: draft, author_name: authorName || undefined,
      }, { params: { k }, headers });
      setDraft("");
      await fetchMessages();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send message.");
    } finally {
      setPosting(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-8 text-center max-w-md">
          <Lock className="w-7 h-7 text-[#FF3B30] mx-auto mb-3" />
          <div className="font-heading font-bold text-xl mb-2">Access denied</div>
          <div className="font-mono text-[11px] text-zinc-400">{error}</div>
        </div>
      </div>
    );
  }
  if (!room) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500 font-mono text-[11px] gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#FFB000]" /> LOADING DEAL ROOM…
      </div>
    );
  }

  const inputs = room.prospect_inputs || {};
  const proposal = room.proposal || {};
  const sym = room.currency_symbol || SYMS[(room.currency_code || "USD").toUpperCase()] || "$";
  const wtSrc = room.walkthrough_id && room.walkthrough_share_token
    ? `${API}/walkthroughs/${room.walkthrough_id}/video?k=${encodeURIComponent(room.walkthrough_share_token)}`
    : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="deal-room-page">
      <header className="border-b border-zinc-900 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <BrandLogo size="md" />
          <span className="text-zinc-700">×</span>
          {room.account_logo_url ? (
            <img src={room.account_logo_url} alt={room.account_name} className="h-7 max-w-[140px] object-contain" />
          ) : (
            <span className="font-heading font-black text-lg truncate">{room.account_name}</span>
          )}
        </div>
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em]">
          <span className={`border px-2 py-1 ${STATUS_COLORS[room.status] || STATUS_COLORS.open}`}>
            <CircleDot className="w-2 h-2 inline mr-1.5" />{room.status}
          </span>
          <span className="text-zinc-500">// PRIVATE DEAL ROOM</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        {/* HERO — proposal headline + their numbers */}
        <section data-testid="deal-room-hero">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// PREPARED FOR {room.account_name?.toUpperCase()}</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-[1.1] mt-3">{proposal.headline}</h1>
          <p className="text-base md:text-lg text-zinc-300 mt-4 leading-relaxed max-w-3xl">{proposal.summary}</p>
        </section>

        {/* Their numbers strip */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="deal-room-numbers">
          <div className="border border-zinc-900 p-4 bg-black/40">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">YOUR REVENUE</div>
            <div className="font-heading font-black text-3xl mt-1 text-white">{sym}{(inputs.revenue_m || 0).toLocaleString()}M</div>
          </div>
          <div className="border border-zinc-900 p-4 bg-black/40">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">YOUR LEAK ESTIMATE</div>
            <div className="font-heading font-black text-3xl mt-1 text-[#FFB000]">{(inputs.leak_pct || 0).toFixed(1)}%</div>
          </div>
          <div className="border border-zinc-900 p-4 bg-black/40">
            <div className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> 12-MONTH RECOVERY</div>
            <div className="font-heading font-black text-3xl mt-1 text-[#00E676]">{fmtCurrency(inputs.computed_lift, sym, 1)}</div>
          </div>
        </section>

        {/* Walkthrough */}
        {wtSrc && (
          <section data-testid="deal-room-walkthrough">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">// THE WALKTHROUGH YOU SAW</div>
            <video controls src={wtSrc} className="w-full bg-zinc-950 border border-zinc-900" />
          </section>
        )}

        {/* Proposal — deliverables + pricing + timeline */}
        <section className="border border-zinc-900 p-6 md:p-8 bg-gradient-to-br from-zinc-950 via-black to-zinc-950" data-testid="deal-room-proposal">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> // 90-DAY PROPOSAL</div>
          <h2 className="font-heading font-black text-2xl tracking-tight mt-2 mb-4">What we&apos;ll deliver together</h2>
          <ul className="space-y-2 mb-6">
            {(proposal.deliverables || []).map((d, i) => (
              <li key={i} className="flex gap-3 items-start" data-testid={`deal-room-deliverable-${i}`}>
                <span className="text-[#FFB000] font-mono text-[12px] mt-0.5">→</span>
                <span className="text-zinc-200 leading-relaxed">{d}</span>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-zinc-900">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">PRICING BAND</div>
              <div className="font-heading font-bold text-base mt-1">{proposal.pricing_band}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Clock className="w-3 h-3" /> TIMELINE</div>
              <div className="font-heading font-bold text-base mt-1">{proposal.timeline_weeks} weeks</div>
            </div>
          </div>
        </section>

        {/* Q&A thread */}
        <section className="border border-zinc-900" data-testid="deal-room-thread">
          <div className="px-5 py-3 border-b border-zinc-900 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#FFB000]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// SHARED Q&amp;A · {messages.length}</span>
          </div>
          <div ref={threadRef} className="max-h-[420px] overflow-y-auto p-5 space-y-3 bg-black/40">
            {messages.length === 0 && (
              <div className="text-center py-10 text-zinc-600 font-mono text-[11px]" data-testid="deal-room-thread-empty">
                No messages yet — be the first to leave a question.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.author === "owner" ? "justify-start" : "justify-end"}`} data-testid={`deal-room-msg-${m.id}`}>
                <div className={`max-w-[80%] border px-4 py-3 ${m.author === "owner" ? "border-zinc-800 bg-zinc-950" : "border-[#FFB000]/30 bg-[#FFB000]/5"}`}>
                  <div className={`font-mono text-[9px] uppercase tracking-widest mb-1 ${m.author === "owner" ? "text-zinc-500" : "text-[#FFB000]"}`}>
                    {m.author_name} · {new Date(m.created_at).toLocaleString()}
                  </div>
                  <div className="text-sm text-zinc-100 whitespace-pre-wrap">{m.body}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-900 p-4 space-y-2">
            {!isOwner && (
              <input
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white"
                data-testid="deal-room-author-input"
              />
            )}
            <div className="flex gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                placeholder="Write a message… (⌘+Enter to send)"
                rows={2}
                className="flex-1 bg-black border border-zinc-800 px-3 py-2 text-sm text-white"
                data-testid="deal-room-message-input"
              />
              <button onClick={post} disabled={posting || !draft.trim()} style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="font-bold font-mono text-[11px] uppercase tracking-widest px-5 hover:opacity-90 disabled:opacity-50 flex items-center gap-2 transition-opacity" data-testid="deal-room-send-btn">
                {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} SEND
              </button>
            </div>
          </div>
        </section>

        {/* Calendar CTA */}
        {room.calendar_url && (
          <section className="border border-zinc-900 p-8 bg-gradient-to-br from-zinc-950 to-black text-center" data-testid="deal-room-calendar-cta">
            <Calendar className="w-7 h-7 text-[#FFB000] mx-auto mb-3" />
            <h2 className="font-heading font-black text-2xl tracking-tight">Let&apos;s lock in the working session.</h2>
            <p className="text-sm text-zinc-400 mt-2 max-w-xl mx-auto">
              Pick a slot and we&apos;ll come prepared with this proposal mapped onto your real systems.
            </p>
            <a href={room.calendar_url} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="inline-flex items-center gap-2 mt-5 font-bold font-mono text-[11px] uppercase tracking-widest px-6 py-3 hover:opacity-90 transition-opacity" data-testid="deal-room-cta-btn">
              {room.calendar_label || "Book a working session"} <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </section>
        )}

        <footer className="text-center font-mono text-[10px] text-zinc-600 pt-4">
          Hosted by {room.owner_name} · {room.owner_email}
        </footer>
      </main>
    </div>
  );
}
